import { headingFont, headingSm } from '@insify/design-system/typography';
import { formatMoney } from '@insify/global-money';
import { formatMoneyToParts } from '@insify/ui-utils';
import clsx from 'clsx';

import styles from './Price.module.css';

import type { MoneyField } from '@insify/global-money';

export type PriceProps = {
  className?: string;
  amountBeforeDiscount?: MoneyField;
  amount?: MoneyField;
  locale: string;
  formatLabel?: (currencySign: string) => string;
  isLoading?: boolean;
};

const integerOrPlaceholder = (value: string, isLoading = false) => (value.length === 0 || isLoading ? '--' : value);
const fragmentOrPlaceholder = (value: string, isLoading = false) => (value.length === 0 || isLoading ? ',--' : value);

/**
 * Change the wrapper font-size to the desired integer size
 * The fraction and label will scale accordingly
 */
export const Price = ({ className, amountBeforeDiscount, amount, locale, formatLabel, isLoading }: PriceProps) => {
  const parts = amount ? formatMoneyToParts(amount, locale) : [];
  const currencySign = parts.find((part) => part.type === 'currency')?.value ?? '';
  const fraction = parts.reduce((acc, part) => {
    switch (part.type) {
      case 'decimal':
      case 'fraction':
        return acc + part.value;
      default:
        return acc;
    }
  }, '');

  const integer = parts.reduce((acc, part) => {
    switch (part.type) {
      case 'integer':
      case 'group':
        return acc + part.value;
      default:
        return acc;
    }
  }, '');

  return (
    <>
      {amountBeforeDiscount !== undefined && amount !== undefined && amountBeforeDiscount.cents > amount?.cents && (
        <p className={clsx(headingSm, styles.discountPrice, 'notranslate')} data-testid='amount-before-discount'>
          {formatMoney(amountBeforeDiscount, locale)}
        </p>
      )}
      <div className={clsx(styles.container, headingFont, className)}>
        <div className={clsx(styles.integer, 'notranslate')}>{integerOrPlaceholder(integer, isLoading)}</div>
        <div className={clsx(styles.fraction, 'notranslate')}>{fragmentOrPlaceholder(fraction, isLoading)}</div>
        <div className={styles.label}>{formatLabel === undefined ? currencySign : formatLabel(currencySign)}</div>
      </div>
    </>
  );
};
